import { Check, Sparkle, PencilSimple } from "@phosphor-icons/react";

/**
 * WorkbookInlineQuestionPick — single-question pick widget rendered INLINE in
 * the workbook article, directly under the question's prose/options/recommendation.
 *
 * 2026-05-23 cycle-5c+25-fix7 (per Owner correction):
 *   The workbook page previously showed all picks bundled in one form at the
 *   top of the page, forcing the Owner to scroll up-and-down between reading
 *   a question and finding its pick widget. This component is injected into
 *   the rendered markdown stream right after each `### Q<n>` section so the
 *   answer input lives next to the question content. A single bottom submit
 *   bar (`WorkbookPickForm`, slimmed down) collects all picks and notes.
 *
 * Layout matches the per-card §1.Q decision footer pattern (CardDecisionFooter):
 *   - Each option card has the radio directly on it (click card = pick that option)
 *   - Per-question free-text textarea below the options
 *   - "MP rec" pill on the recommended option
 *
 * Hidden when:
 *   - state.acceptAll is true (the whole workbook is being accepted in one go)
 */
export default function WorkbookInlineQuestionPick({ question, state }) {
  if (!question) return null;
  if (state.acceptAll) return null;

  const qState = state.picks[question.number] || { letter: null, free_text: "" };
  const answered = state.isAnswered(question.number);

  return (
    <div
      className="rounded-md border my-4 p-2.5 sm:p-3"
      style={{
        borderColor: "hsl(var(--primary) / 0.35)",
        background: "hsl(var(--primary) / 0.03)",
      }}
      data-testid={`mp-inline-pick-${question.number}`}
    >
      <div className="flex items-baseline gap-2 mb-2">
        <span
          className="text-mono"
          style={{
            fontSize: "0.6rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 700,
            color: "hsl(var(--primary))",
          }}
        >
          Your pick on {question.number}
        </span>
        {answered && (
          <span
            className="inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 ml-auto"
            style={{
              fontSize: "0.55rem",
              fontWeight: 600,
              background: "hsl(var(--status-success) / 0.1)",
              color: "hsl(var(--status-success))",
              borderColor: "hsl(var(--status-success) / 0.4)",
            }}
            data-testid={`mp-inline-answered-${question.number}`}
          >
            <Check size={9} weight="bold" />
            Answered
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        {question.options.map((opt) => {
          const checked = qState.letter === opt.letter;
          return (
            <label
              key={opt.letter}
              className="flex items-start gap-2 cursor-pointer rounded-md border p-2 transition-colors"
              style={{
                borderColor: checked
                  ? "hsl(var(--primary) / 0.7)"
                  : opt.is_mp_rec
                    ? "hsl(var(--primary) / 0.35)"
                    : "hsl(var(--border))",
                background: checked
                  ? "hsl(var(--primary) / 0.10)"
                  : opt.is_mp_rec
                    ? "hsl(var(--primary) / 0.04)"
                    : "hsl(var(--background))",
              }}
              data-testid={`mp-inline-option-${question.number}-${opt.letter}`}
            >
              <input
                type="radio"
                name={`inline-pick-${question.number}`}
                value={opt.letter}
                checked={checked}
                onChange={() => state.setLetter(question.number, opt.letter)}
                className="mt-1 shrink-0"
              />
              <span
                className="text-mono font-bold shrink-0"
                style={{
                  fontSize: "0.7rem",
                  color: checked
                    ? "hsl(var(--primary))"
                    : opt.is_mp_rec
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground))",
                  minWidth: "1.4rem",
                }}
              >
                ({opt.letter})
              </span>
              <p
                className="text-xs leading-snug flex-1"
                style={{
                  color: "hsl(var(--foreground))",
                  fontWeight: checked ? 600 : 400,
                }}
              >
                {opt.summary}
              </p>
              {opt.is_mp_rec && (
                <span
                  className="inline-flex items-center gap-0.5 rounded-md border px-1 py-0.5 text-mono shrink-0"
                  style={{
                    fontSize: "0.55rem",
                    fontWeight: 600,
                    background: "hsl(var(--primary) / 0.12)",
                    color: "hsl(var(--primary))",
                    borderColor: "hsl(var(--primary) / 0.4)",
                  }}
                >
                  <Sparkle size={8} weight="fill" />
                  MP rec
                </span>
              )}
            </label>
          );
        })}
      </div>

      <div className="mt-2">
        <div className="flex items-center gap-1 mb-0.5">
          <PencilSimple
            size={10}
            style={{ color: "hsl(var(--muted-foreground))" }}
          />
          <label
            className="text-mono"
            htmlFor={`inline-free-text-${question.number}`}
            style={{
              fontSize: "0.6rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
              color: "hsl(var(--muted-foreground))",
            }}
          >
            Or write your own response on {question.number}
          </label>
        </div>
        <textarea
          id={`inline-free-text-${question.number}`}
          value={qState.free_text || ""}
          onChange={(e) => state.setFreeText(question.number, e.target.value)}
          rows={2}
          placeholder={`Clarify, push back, or write your own answer to ${question.number}.`}
          className="w-full rounded-md border px-2 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{
            fontSize: "0.75rem",
            background: "hsl(var(--background))",
            borderColor: (qState.free_text || "").trim()
              ? "hsl(var(--primary) / 0.4)"
              : "hsl(var(--border))",
            color: "hsl(var(--foreground))",
            resize: "vertical",
          }}
          data-testid={`mp-inline-free-text-${question.number}`}
        />
      </div>
    </div>
  );
}
